import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface ApiKey {
  keyPreview: string;
  tier: 'mobile' | 'admin' | 'dev' | 'internal';
  name: string;
  rateLimit: number;
  createdAt: string;
  lastUsed?: string;
  enabled: boolean;
  allowedEndpoints?: string[];
}

interface GeneratedKey {
  key: string;
  tier: string;
  name: string;
  rateLimit: number;
  createdAt: string;
  enabled: boolean;
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<GeneratedKey | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Map<string, string>>(new Map());
  const [revealingKey, setRevealingKey] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    tier: 'mobile' as 'mobile' | 'admin' | 'dev' | 'internal',
    rateLimit: 100,
  });

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const response = await api.listApiKeys() as { success: boolean; keys: ApiKey[]; total: number };
      setKeys(response.keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    try {
      const response = await api.generateApiKey(formData) as { 
        success: boolean; 
        key: GeneratedKey;
        envFormat: string;
        message: string;
      };
      
      if (response.success) {
        setGeneratedKey(response.key);
        setShowGenerateDialog(false);
        setFormData({ name: '', tier: 'mobile', rateLimit: 100 });
        loadKeys(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to generate key:', error);
      alert('Failed to generate API key: ' + (error as Error).message);
    }
  };

  const handleRevokeKey = async (keyPreview: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const keyPrefix = keyPreview.split('...')[0];
      await api.revokeApiKey(keyPrefix);
      loadKeys(); // Refresh the list
    } catch (error) {
      console.error('Failed to revoke key:', error);
      alert('Failed to revoke API key: ' + (error as Error).message);
    }
  };

  const handleEnableKey = async (keyPreview: string) => {
    try {
      const keyPrefix = keyPreview.split('...')[0];
      await api.enableApiKey(keyPrefix);
      loadKeys(); // Refresh the list
    } catch (error) {
      console.error('Failed to enable key:', error);
      alert('Failed to enable API key: ' + (error as Error).message);
    }
  };

  const getTierColor = (tier: string) => {
    const colors = {
      mobile: 'bg-blue-100 text-blue-800',
      admin: 'bg-purple-100 text-purple-800',
      dev: 'bg-green-100 text-green-800',
      internal: 'bg-orange-100 text-orange-800',
    };
    return colors[tier as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTierLabel = (tier: string) => {
    const labels = {
      mobile: 'Mobile',
      admin: 'Admin',
      dev: 'Development',
      internal: 'Internal',
    };
    return labels[tier as keyof typeof labels] || tier;
  };

  const getDefaultRateLimit = (tier: string) => {
    const defaults = {
      mobile: 100,
      admin: 1000,
      dev: 10,
      internal: 10000,
    };
    return defaults[tier as keyof typeof defaults] || 100;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatLastUsed = (dateString?: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    
    return formatDate(dateString);
  };

  const handleRevealKey = async (keyPreview: string) => {
    if (revealedKeys.has(keyPreview)) {
      // Already revealed, just toggle visibility
      const newRevealedKeys = new Map(revealedKeys);
      newRevealedKeys.delete(keyPreview);
      setRevealedKeys(newRevealedKeys);
      return;
    }

    // Show security warning
    const confirmed = confirm(
      '⚠️ SECURITY WARNING\n\n' +
      'You are about to reveal the full API key. This key provides access to your system.\n\n' +
      '• This action will be logged\n' +
      '• Only proceed if you need to copy or view the key\n' +
      '• Never share this key publicly\n\n' +
      'Do you want to continue?'
    );

    if (!confirmed) return;

    try {
      setRevealingKey(keyPreview);
      const keyPrefix = keyPreview.split('...')[0];
      const response = await api.revealApiKey(keyPrefix) as { 
        success: boolean; 
        key: string;
        message: string;
      };
      
      if (response.success) {
        const newRevealedKeys = new Map(revealedKeys);
        newRevealedKeys.set(keyPreview, response.key);
        setRevealedKeys(newRevealedKeys);
      }
    } catch (error) {
      console.error('Failed to reveal key:', error);
      alert('Failed to reveal API key: ' + (error as Error).message);
    } finally {
      setRevealingKey(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <span>🔑</span>
            API Key Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage API keys for accessing the LinePe Content API
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadKeys}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <span>🔄</span>
            Refresh
          </button>
          <button
            onClick={() => setShowGenerateDialog(true)}
            className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <span>➕</span>
            Generate New Key
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Total Keys</div>
          <div className="text-2xl font-bold text-gray-900">{keys.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Active Keys</div>
          <div className="text-2xl font-bold text-green-600">
            {keys.filter(k => k.enabled).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Disabled Keys</div>
          <div className="text-2xl font-bold text-red-600">
            {keys.filter(k => !k.enabled).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Recently Used</div>
          <div className="text-2xl font-bold text-blue-600">
            {keys.filter(k => k.lastUsed).length}
          </div>
        </div>
      </div>

      {/* API Keys List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-linepe-orange mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading API keys...</p>
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <span className="text-6xl">🔑</span>
          <p className="text-gray-600 mt-4 text-lg">No API keys found</p>
          <p className="text-gray-500 text-sm mt-2">
            Generate your first API key to get started
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {keys.map((key) => (
            <div
              key={key.keyPreview}
              className={`bg-white p-6 rounded-lg border-2 ${
                key.enabled ? 'border-gray-200' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Key Preview */}
                  <div className="flex items-center gap-3 mb-3">
                    <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded flex-1">
                      {revealedKeys.has(key.keyPreview) ? revealedKeys.get(key.keyPreview) : key.keyPreview}
                    </code>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded ${getTierColor(
                        key.tier
                      )}`}
                    >
                      {getTierLabel(key.tier)}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                        key.enabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {key.enabled ? '● Active' : '○ Disabled'}
                    </span>
                  </div>

                  {/* Reveal/Copy Buttons */}
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => handleRevealKey(key.keyPreview)}
                      disabled={revealingKey === key.keyPreview}
                      className={`px-3 py-1 text-xs rounded transition-colors ${
                        revealedKeys.has(key.keyPreview)
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={revealedKeys.has(key.keyPreview) ? 'Hide full key' : 'Reveal full key'}
                    >
                      {revealingKey === key.keyPreview ? '⏳' : revealedKeys.has(key.keyPreview) ? '🔒 Hide' : '👁️ Reveal'}
                    </button>
                    {revealedKeys.has(key.keyPreview) && (
                      <button
                        onClick={() => copyToClipboard(revealedKeys.get(key.keyPreview)!)}
                        className="px-3 py-1 text-xs bg-green-100 text-green-800 hover:bg-green-200 rounded transition-colors"
                        title="Copy to clipboard"
                      >
                        📋 Copy
                      </button>
                    )}
                  </div>

                  {/* Security Warning when revealed */}
                  {revealedKeys.has(key.keyPreview) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
                      <p className="text-xs text-yellow-800">
                        ⚠️ <strong>Full key is visible.</strong> This action has been logged. Never share this key publicly.
                      </p>
                    </div>
                  )}

                  {/* Key Name */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {key.name}
                  </h3>

                  {/* Key Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Rate Limit</div>
                      <div className="font-semibold text-gray-900">
                        {key.rateLimit} req/min
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Created</div>
                      <div className="font-semibold text-gray-900">
                        {formatDate(key.createdAt)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Last Used</div>
                      <div className="font-semibold text-gray-900">
                        {formatLastUsed(key.lastUsed)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-600">Endpoints</div>
                      <div className="font-semibold text-gray-900">
                        {key.allowedEndpoints ? key.allowedEndpoints.length : 'All'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 ml-4">
                  {key.enabled ? (
                    <button
                      onClick={() => handleRevokeKey(key.keyPreview)}
                      className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                      title="Revoke Key"
                    >
                      🗑️ Revoke
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEnableKey(key.keyPreview)}
                      className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      title="Enable Key"
                    >
                      ▶️ Enable
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate Key Dialog */}
      {showGenerateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Generate New API Key
            </h2>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Production Mobile App"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
                />
              </div>

              {/* Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tier
                </label>
                <select
                  value={formData.tier}
                  onChange={(e) => {
                    const tier = e.target.value as 'mobile' | 'admin' | 'dev' | 'internal';
                    setFormData({
                      ...formData,
                      tier,
                      rateLimit: getDefaultRateLimit(tier),
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
                >
                  <option value="mobile">Mobile (100 req/min)</option>
                  <option value="admin">Admin (1000 req/min)</option>
                  <option value="dev">Development (10 req/min)</option>
                  <option value="internal">Internal (10000 req/min)</option>
                </select>
              </div>

              {/* Rate Limit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate Limit (requests per minute)
                </label>
                <input
                  type="number"
                  value={formData.rateLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      rateLimit: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowGenerateDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateKey}
                disabled={!formData.name}
                className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Key Dialog */}
      {generatedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              ✅ API Key Generated Successfully!
            </h2>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Important:</strong> This is the only time you will see this key.
                Store it securely - it cannot be retrieved again.
              </p>
            </div>

            <div className="space-y-4">
              {/* Key Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <div className="flex gap-2">
                  <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono overflow-x-auto">
                    {generatedKey.key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(generatedKey.key)}
                    className="px-3 py-2 bg-linepe-orange text-white rounded hover:bg-orange-600 transition-colors"
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <div className="bg-gray-100 px-3 py-2 rounded text-sm">
                    {generatedKey.name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tier
                  </label>
                  <div className="bg-gray-100 px-3 py-2 rounded text-sm">
                    {getTierLabel(generatedKey.tier)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate Limit
                  </label>
                  <div className="bg-gray-100 px-3 py-2 rounded text-sm">
                    {generatedKey.rateLimit} req/min
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <div className="bg-gray-100 px-3 py-2 rounded text-sm">
                    {formatDate(generatedKey.createdAt)}
                  </div>
                </div>
              </div>

              {/* Usage Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  📖 How to Use
                </h3>
                <p className="text-sm text-blue-800 mb-2">
                  Include this key in your API requests:
                </p>
                <code className="block bg-blue-100 px-3 py-2 rounded text-xs font-mono overflow-x-auto">
                  curl -H "x-api-key: {generatedKey.key}" https://your-api.com/api/render
                </code>
              </div>
            </div>

            {/* Close Button */}
            <div className="mt-6">
              <button
                onClick={() => setGeneratedKey(null)}
                className="w-full px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                I've Saved This Key Securely
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

